/**
 * About Page - WNC Localized
 *
 * Why Western North Carolina, neighborhood breakdown, and Coupling network explanation.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Footer, GradientHero, SEO, breadcrumbSchema } from '@coupling/ui';
import Header from '../components/Header';
import { useBooking } from '../components/BookingProvider';
import { seoConfig } from '../config/seo';
import {
  ownPosAction,
  ownPosActionThirdPerson,
  primaryDemoCta,
  tryCouplingCta,
} from '../content/siteMessaging';

export const About: React.FC = () => {
  const navigate = useNavigate();
  const booking = useBooking();
  const values = [
    {
      icon: '🤝',
      title: 'Community First',
      description:
        'WNC thrives on local connections. We strengthen that by keeping spending within our mountain communities.',
    },
    {
      icon: '🔓',
      title: 'Open & Interoperable',
      description:
        'No vendor lock-in. Run checkout in Coupling POS on your phone. Rewards can work across WNC merchants you choose.',
    },
    {
      icon: '⚡',
      title: 'Simple & Accessible',
      description:
        'Powerful tools should be easy to use. Tap to Pay on the phone you already have — as simple as taking a card.',
    },
    {
      icon: '🛡️',
      title: 'Trustworthy & Transparent',
      description:
        'Clear pricing, no hidden fees, and honest communication. Your success is our success.',
    },
  ];

  const neighborhoods = [
    {
      name: 'West Asheville',
      description: 'Haywood Road\'s independent shops, restaurants, and breweries forming a natural merchant network.',
    },
    {
      name: 'South Slope',
      description: 'The brewery district where craft beverage makers and eateries already share customers.',
    },
    {
      name: 'River Arts District',
      description: 'Studios, galleries, and creative businesses with a built-in community of makers and buyers.',
    },
    {
      name: 'Downtown Asheville',
      description: 'The heart of WNC commerce, from Pack Square to Lexington Avenue.',
    },
    {
      name: 'Black Mountain & Weaverville',
      description: 'Small-town main streets with tight-knit merchant communities ready for shared rewards.',
    },
    {
      name: 'Brevard & Hendersonville',
      description: 'Growing communities in Transylvania and Henderson counties with active local business scenes.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`About Coupling WNC for merchants - ${seoConfig.siteName}`}
        description={`Learn how Coupling WNC helps Western North Carolina merchants take cards on the phone they already have, ${ownPosActionThirdPerson}, book appointments, and run local rewards.`}
        canonical={`${seoConfig.siteUrl}/about`}
        ogImage={seoConfig.defaultOgImage}
        ogUrl={`${seoConfig.siteUrl}/about`}
        jsonLd={breadcrumbSchema([
          { name: 'Home', url: seoConfig.siteUrl },
          { name: 'About', url: `${seoConfig.siteUrl}/about` },
        ])}
      />
      <Header />

      <GradientHero
        title="Start with your shop. Run checkout tomorrow."
        description={`Help your WNC shop take cards on the phone you already have, ${ownPosAction}, book appointments, and add rewards — from Hendersonville to Haywood Road.`}
      />

      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-neutral-900 mb-6">Start with the counter you already have</h2>
            <p className="text-2xl text-neutral-700 leading-relaxed">
              Coupling WNC helps independent merchants take payments, book appointments, and run rewards
              that fit the way they already sell, then turn those repeat visits into stronger neighborhood relationships.
            </p>
          </div>

          <div className="mt-16">
            <img src="/illustrations/community-network.webp" alt="Connected local shops forming a community network" className="w-full h-auto max-w-2xl mx-auto rounded-2xl" width={1024} height={1024} loading="lazy" />
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-bgSubtle">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-neutral-900 mb-6">The Problem We're Solving</h2>
              <div className="space-y-4">
                <p className="text-lg text-neutral-700">
                  Western North Carolina has an incredible concentration of independent businesses — coffee shops,
                  breweries, galleries, farm-to-table restaurants, outdoor outfitters. These businesses are the
                  heartbeat of our communities.
                </p>
                <p className="text-lg text-neutral-700">
                  Most merchants still have to choose between punch cards, gift cards, or one-off discounts that do not
                  connect to each other. The result is more work for staff and less momentum for repeat customers.
                </p>
                <p className="text-lg text-neutral-700 font-medium">
                  Start by taking cards on the phone you already have, running your entire checkout in Coupling POS, and letting
                  customers book the next visit. Once checkout works, you can decide where local rewards connections make sense.
                </p>
              </div>
            </div>
            <div>
              <Card className="p-8 bg-white">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                      Closed-Loop Systems
                    </h3>
                    <ul className="space-y-2 text-neutral-700">
                      <li>• Staff juggle separate rewards and gift card tools</li>
                      <li>• Customers forget what they have or how to redeem it</li>
                      <li>• Merchants cannot extend a clear offer across channels</li>
                      <li>• Local partnerships stay manual and hard to test</li>
                    </ul>
                  </div>
                  <div className="border-t border-neutral-200 pt-6">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-2">
                      Coupling WNC's Approach
                    </h3>
                    <ul className="space-y-2 text-neutral-700">
                      <li>• One shop system for Tap to Pay, catalog, checkout, booking, and rewards</li>
                      <li>• Mix cash, card, and rewards through the workflow you already use</li>
                      <li>• Optional customer transfer and partner acceptance when you are ready</li>
                      <li>• Stronger neighborhood spending across WNC over time</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">WNC Neighborhoods</h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Each neighborhood has its own character and merchant community. Coupling connects them all.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {neighborhoods.map((hood) => (
              <Card key={hood.name} className="p-6">
                <h3 className="text-xl font-semibold text-neutral-900 mb-3">{hood.name}</h3>
                <p className="text-neutral-700">{hood.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-bgSubtle">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img src="/illustrations/global-network.png" alt="Connected local communities forming a global network" className="w-full h-auto max-w-md mx-auto rounded-2xl" width={1024} height={1024} loading="lazy" />
            </div>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-primary/10 text-brand-primary text-sm font-semibold">
                <span>Part of Something Bigger</span>
              </div>
              <h2 className="text-3xl font-bold text-neutral-900">The Coupling Network</h2>
              <p className="text-lg text-neutral-700">
                Coupling WNC is part of the larger{' '}
                <a href="https://cplng.org" target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline font-medium">
                  Coupling network
                </a>{' '}
                — an open shop system for local checkout, appointments, and rewards.
              </p>
              <p className="text-lg text-neutral-700">
                After a merchant offer is working locally, the broader network gives WNC businesses a way to connect
                with nearby partners without getting locked into a closed vendor system.
              </p>
              <p className="text-lg text-neutral-700">
                Every local Coupling community can connect with others over time. That means
                the long-term network story supports your shop instead of replacing it.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">Our Values</h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              These principles guide every decision we make.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <Card key={value.title} className="p-6 text-center">
                <div className="text-5xl mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-3">{value.title}</h3>
                <p className="text-neutral-700">{value.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-br from-brand-primary via-brand-primary-dark to-brand-accent text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Join the WNC Merchant Network</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Whether you're a massage practice in Hendersonville, a farm stand, a chiropractor, or a shop
            on Haywood Road — if you sell from a phone, Coupling POS is built for you.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="secondary" size="lg" onClick={() => { booking.open('demo'); }}>
              {primaryDemoCta}
            </Button>
            <Button
              variant="ghost"
              size="lg"
              className="bg-white/10 hover:bg-white/20 text-white"
              onClick={() => { navigate('/contact'); }}
            >
              {tryCouplingCta}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
